import { handleJSNative } from "../error";
import * as logger from "../logger";
import { Source } from "../logger";

export const NOT_NATIVE_APP_MSG = "Not a Native App";
export const NATIVESDK_UNDEFINED_MSG = "window.geotabModules is undefined";
export const CORDOVA_UNDEFINED_MSG = "window.cordova is undefined";

export enum AppType {
    WebApp,
    CordovaApp,
    NativeSdk
}

export enum GeotabAppId {
    GEOTAB_DRIVE = 0,
    MYGEOTAB
}

/**
 * Is Geotab Drive in a native application
 */
export function isNativeApp (): boolean {
    return !!window.device;
}

export function getAppType (): AppType {
    return (!!window.device && window.device.appType) || AppType.WebApp;
}

/**
 * Returns the cordova device information (model, platform, uuid, version)
 */
export function getDeviceInfo () {
    switch (getAppType()) {
        case AppType.CordovaApp:
        case AppType.NativeSdk:
            return window.device;
        case AppType.WebApp:
        default:
            break;
    }

    if (window.DRIVE_DEBUG) {
        let fakeMobileDevice = window.DRIVE_DEBUG.FAKE_MOBILE_DEVICE;
        if (fakeMobileDevice && fakeMobileDevice !== "None") {
            return {
                model: "FakeDevice",
                platform: fakeMobileDevice,
                uuid: "abcdef0123456789",
                version: "0.0"
            };
        }
    }
    return undefined;
}

export let isIphone = function() {
    let info = getDeviceInfo();
    let platform = info && info.platform;
    return platform && platform === "iOS";
};

/**
 * Returns the type of internet connection the app is currently using
 */
export function getConnectionType () {
    switch (getAppType()) {
        case AppType.CordovaApp:
            if ((window as any).navigator.connection) {
                return (window as any).navigator.connection.type;
            }
            break;
        case AppType.NativeSdk:
            if (window.geotabModules) {
                if (window.geotabModules.connectivity.state) {
                    return window.geotabModules.connectivity.state.type;
                }
            }
            break;
        case AppType.WebApp:
        default:
            break;
    }
    return "Unknown";
}

/**
 * Keep the screen on
 */
export function keepScreenOn (keep: boolean) {
    try {
        switch (getAppType()) {
            case AppType.CordovaApp:
                if (window.plugins && window.plugins.insomnia) {
                    if (keep) {
                        window.plugins.insomnia.keepAwake();
                    } else {
                        window.plugins.insomnia.allowSleepAgain();
                    }
                }
                break;
            case AppType.NativeSdk:
                if (window.geotabModules) {
                    window.geotabModules.screen.keepAwake(keep, (error) => {
                        if (error) {
                            logger.error(logger.Source.NATIVE, `keepScreenOn: Error ${ error.message }`);
                        }
                    });
                }
                break;
            case AppType.WebApp:
            default:
                break;
        }
    } catch (error) {
        handleJSNative(error instanceof Error ? error : new Error(error), true, "nativeUnhandledException");
    }
}

/**
 * Use backgroundMode plugin to return whether app is running in background. If not native app, return false
 */
export function isAppInBackground (): boolean {
    try {
        switch (getAppType()) {
            case AppType.CordovaApp:
                return window.cordova.plugins.backgroundMode.isActive();
            case AppType.NativeSdk:
                return !!window.geotabModules && !!window.geotabModules.app.background;
            case AppType.WebApp:
            default:
                break;
        }
        return false;
    } catch (error) {
        handleJSNative(error instanceof Error ? error : new Error(error), true, "nativeUnhandledException");
        return false;
    }
}

/**
 * Private check on whether the backgroundMode is enabled (allows background processing)
 */
export function isBackgroundModeEnabled(): boolean {
    try {
        switch (getAppType()) {
            case AppType.CordovaApp:
                return window.cordova.plugins.backgroundMode.isEnabled();
            case AppType.NativeSdk:
                return !!window.geotabModules && !!window.geotabModules.app;
            case AppType.WebApp:
            default:
                break;
        }
        return false;
    } catch (error) {
        handleJSNative(error instanceof Error ? error : new Error(error), true, "nativeUnhandledException");
        return false;
    }
}

export function shouldSetPiP() {
    // PiP Module exist only in Android, otherwise it's not enabled.
    return window.geotabModules?.pip && (typeof window.geotabModules.pip["setStatusPiP"] === "function");
}

export function setPipMode(pipStatus: boolean) {
    if (shouldSetPiP()) {
        window.geotabModules?.pip.setStatusPiP({isEnabled: pipStatus}, (error) => {
            if (error) {
                logger.error(Source.NATIVE, `pip functionality Error: ${ error.message }`);
            }
        });
    }
}
